import { MutationCache, QueryClient } from "@tanstack/react-query";
import { isApiError } from "../api/client.ts";
import { queryKeys } from "../hooks/keys.ts";

/**
 * One query client for the app.
 *
 * The retry rule is the only opinion here: a 4xx means the request was wrong and will be wrong
 * again, so retrying it only makes the screen slower to tell the truth. Anything else (a dropped
 * UAE mobile connection, a cold API) is worth two more attempts.
 *
 * One more: a write refused with `BILLING_READ_ONLY` means the business's plan changed under an open
 * page (the grace ended while the owner worked). The business list carries the billing summary, so
 * reloading it brings the read-only banner and turns the write buttons off at once.
 *
 * And the To do list: any job on it (an answer, a person who joined, a session to decide) can end
 * with a write on another page, so every successful write reloads it. It is one small read, and no
 * new job has to remember to refresh it.
 */
export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    mutationCache: new MutationCache({
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: queryKeys.todo.all });
      },
      onError: (error) => {
        if (isApiError(error) && error.code === "BILLING_READ_ONLY") {
          void client.invalidateQueries({ queryKey: queryKeys.businesses });
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
  return client;
}
